import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  Alert,
  Box,
  Button,
  Container,
  Snackbar,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { ConfirmIcon, LeftArrowIcon } from 'src/v3/core/icons'
import {
  fetchEvents,
  fetchQuiz,
  fetchQuizQuestions,
  selectEventById,
  selectEventsStatus,
  selectQuestionsByQuiz,
  selectQuestionsStatusByQuiz,
  selectQuizByEvent,
  selectQuizStatusByEvent,
} from './events.slice'
import axios, { pluckData } from 'src/utilities/http-client'
import { QuizBuilder } from 'src/v3/components/quiz/quiz-builder'
import Loading from 'src/v3/components/loading'

let Quiz = ({
  setStep,
}: {
  setStep: React.Dispatch<React.SetStateAction<'quiz' | 'sessions' | 'details'>>
}): ReactElement => {
  let dispatch = useAppDispatch()
  let { eventId } = useParams()
  let eventID = eventId || ''

  let event = useAppSelector(selectEventById(eventID))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let quizID = quiz?.id || ''
  let questions = useAppSelector(selectQuestionsByQuiz(quizID))
  let questionsStatus = useAppSelector(selectQuestionsStatusByQuiz(quizID))
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if ((eventID && !quizStatus) || quizStatus === 'uninitialized') {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  useEffect(() => {
    if ((quizID && !questionsStatus) || questionsStatus === 'uninitialized') {
      dispatch(fetchQuizQuestions(quizID))
    }
  }, [questionsStatus, quizID])

  let buttonBar = (
    <Box
      sx={{
        mt: 5,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Button
        sx={{ color: 'text.primary', textTransform: 'none' }}
        startIcon={<LeftArrowIcon />}
        onClick={() => setStep('sessions')}
      >
        Previous: Sessions
      </Button>
      <Button
        type="submit"
        variant="contained"
        sx={{ textTransform: 'none' }}
        endIcon={<ConfirmIcon />}
      >
        Save Changes
      </Button>
    </Box>
  )

  return (
    <Container maxWidth="xl">
      <Typography variant="h5" sx={{ mb: 3 }}>
        Event Quiz
      </Typography>
      {!quiz ? (
        <Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            If you would like for your event to have a quiz, click the button
            below to begin building a one for this event's attendees to take at
            its conclusion.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="secondary"
            loading={loading}
            onClick={() => {
              setLoading(true)
              axios
                .post('/api/event/quiz', {
                  eventID,
                  passingScore: 0,
                  displayedQuestions: 0,
                  randomizeQuestions: true,
                })
                .then(() => dispatch(fetchQuiz(eventID)))
                .catch((err) =>
                  setErrorMessage(
                    err?.response?.data || `Quiz creation failed`,
                  ),
                )
                .finally(() => setLoading(false))
            }}
          >
            Create Quiz
          </Button>
          {buttonBar}
        </Box>
      ) : (
        <QuizBuilder
          quiz={quiz}
          fetchQuiz={fetchQuiz}
          fetchQuizBy={eventID}
          questionData={questions}
          fetchQuestionData={fetchQuizQuestions}
          apiPath="/api/event"
          submitButton={buttonBar}
        />
      )}
      <Loading entities={[eventsStatus, quizStatus, questionsStatus]} />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default Quiz
